import { CheckSquare, Square, XClose } from '@aktiia/design-system';
import { MouseEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Devices } from '../../../../../constants';
import { usePatient } from '../../../../../contexts';
import { Device } from '../../../../../enums';
import { useDateRangeText, usePhysiologicalData } from '../../../../../features/physiological-data';
import { Button } from '../../../../common';
import { IDownloadReportPopoverProps } from './types';

export const DownloadReportPopover = ({ onClose, onDownload }: IDownloadReportPopoverProps) => {
  const { t } = useTranslation();
  const { rangeType } = usePhysiologicalData();
  const { visibleTables } = usePatient();
  const [selectedDevices, setSelectedDevices] = useState({
    bracelet: visibleTables.includes(Device.BRACELET),
    cuff: visibleTables.includes(Device.CUFF),
  });
  const dateText = useDateRangeText();

  const handleInteralClick = (event: MouseEvent) => event.stopPropagation();
  const toggleSelectedDevice = (name: Device) => {
    setSelectedDevices({
      ...selectedDevices,
      [name]: !selectedDevices[name],
    });
  };
  const handleDownload = () => {
    onClose();

    const devices = [selectedDevices.bracelet && Device.BRACELET, selectedDevices.cuff && Device.CUFF].filter(
      (d) => !!d,
    ) as Device[];
    onDownload(devices);
  };

  return (
    <div
      className="w-[240px] bg-background-base-primary border border-border-light rounded-xl shadow-high"
      onClick={handleInteralClick}
    >
      <div className="flex items-center justify-between py-3 px-4 border-b border-border-divider">
        <span className="typography-title-3 text-text-primary">{t('patient.report.title')}</span>
        <XClose className="cursor-pointer scale-75" onClick={onClose} />
      </div>

      <div className="py-3 px-4">
        <p className="typography-paragraph-3 text-text-secondary">
          {t('patient.report.subtitle', { range: t(`common.dateRange.${rangeType}.label`) })}
        </p>
        <p className="typography-heading-6 text-text-primary mb-6">{dateText}</p>
        <p className="typography-paragraph-3 text-text-secondary mb-2">{t('patient.report.device.title')}</p>
        <ul className="mb-6">
          {Devices.map((name) => (
            <li
              key={name}
              className="h-[40px] flex items-center justify-between cursor-pointer"
              onClick={() => toggleSelectedDevice(name)}
            >
              <span className="typography-inline-highlight-3 text-text-primary">
                {t(`patient.report.device.${name}`)}
              </span>
              {selectedDevices[name] ? <CheckSquare className="scale-75" /> : <Square className="scale-75" />}
            </li>
          ))}
        </ul>
        <Button
          text={t('patient.report.button.download')}
          size="sm"
          className="w-full justify-center"
          onClick={handleDownload}
        />
      </div>
    </div>
  );
};
